/**
 * TimescaleDB API Client
 * Frontend service for consuming optimized time-series analytics
 * Sprint 2: Frontend Dashboard Integration
 */

import apiClient from "./apiClient";

export interface BackgroundTrendData {
  timestamp: string;
  delta_power: number;
  theta_power: number;
  alpha_power: number;
  beta_power: number;
  gamma_power: number;
  total_power: number;
  alpha_variability: number;
  suppression_ratio: number | null;
  asymmetry_index: number | null;
  dominant_frequency: number | null;
  reactivity_score: number | null;
  background_pattern: string;
}

export interface SeizureBurdenData {
  window_start: string;
  window_end: string;
  total_seizure_seconds: number;
  seizure_count: number;
  burden_percent: number;
  severity_score: number | null;
}

export interface AlertData {
  alert_type: string;
  severity: string;
  count: number;
  latest_timestamp: string;
}

export interface ElectrodeQualityData {
  channel_name: string;
  avg_impedance: number;
  status: string;
  measurement_count: number;
}

export interface ICUDashboardData {
  study: {
    study_id: string;
    patient_id: string;
    study_date: string;
    status: string;
  };
  current_status: {
    last_update: string;
    active_alerts: number;
    recording_duration_hours: number;
    quality_score: number | null;
  };
  trends: {
    latest: BackgroundTrendData | null;
    hourly: BackgroundTrendData[];
  };
  seizures: {
    total_count: number;
    total_burden_percent: number;
    daily: SeizureBurdenData[];
    /** CX-G04: sliding window minutes (default 5). */
    window_minutes?: number;
    ncse_level?: "none" | "elevated" | "suspected_ncse_ese";
    assistive_alert_message?: string | null;
  };
  alerts: {
    total_count: number;
    by_severity: AlertData[];
    hourly: any[];
  };
  electrode_quality: {
    total_channels: number;
    ideal_count: number;
    warning_count: number;
    critical_count: number;
    channels: ElectrodeQualityData[];
  };
}

export interface PowerBandStats {
  band: string;
  avg_power: number;
  min_power: number;
  max_power: number;
  std_dev: number;
}

export interface TimescaleStatus {
  connected: boolean;
  version: string;
  hypertables: number;
  compression_enabled: boolean;
  last_aggregate_refresh: string;
}

export interface CompressionStats {
  table_name: string;
  total_chunks: number;
  compressed_chunks: number;
  compression_ratio: number;
  uncompressed_bytes: string;
  compressed_bytes: string;
}

class TimescaleAPIClient {
  /**
   * Check TimescaleDB health and status
   */
  async getStatus(): Promise<TimescaleStatus> {
    const response = await apiClient.get("/timescale/status");
    return response.data.data;
  }

  /**
   * Get comprehensive ICU dashboard data (single API call)
   */
  async getICUDashboard(studyId: string): Promise<ICUDashboardData> {
    const response = await apiClient.get(`/timescale/dashboard/${studyId}`);
    return response.data.data;
  }

  /**
   * Get hourly power band trends
   */
  async getHourlyTrends(
    studyId: string,
    hours: number = 24,
  ): Promise<BackgroundTrendData[]> {
    const response = await apiClient.get(
      `/timescale/trends/${studyId}/hourly`,
      { params: { hours } },
    );
    return response.data.data;
  }

  /**
   * Get real-time trends (last N minutes)
   */
  async getRealtimeTrends(
    studyId: string,
    minutes: number = 5,
  ): Promise<BackgroundTrendData[]> {
    const response = await apiClient.get(
      `/timescale/trends/${studyId}/realtime`,
      { params: { minutes } },
    );
    return response.data.data;
  }

  /**
   * Get aggregated power band statistics
   */
  async getPowerBandStats(
    studyId: string,
    hours: number = 24,
  ): Promise<PowerBandStats[]> {
    const response = await apiClient.get(
      `/timescale/trends/${studyId}/power-bands`,
      { params: { hours } },
    );
    return response.data.data;
  }

  /**
   * Get daily seizure burden
   */
  async getDailySeizureBurden(
    studyId: string,
    days: number = 7,
  ): Promise<SeizureBurdenData[]> {
    const response = await apiClient.get(
      `/timescale/seizures/${studyId}/daily`,
      { params: { days } },
    );
    return response.data.data;
  }

  /**
   * Get seizure trend over time
   */
  async getSeizureTrend(
    studyId: string,
    days: number = 7,
  ): Promise<{ date: string; total_seconds: number; count: number }[]> {
    const response = await apiClient.get(
      `/timescale/seizures/${studyId}/trend`,
      { params: { days } },
    );
    return response.data.data;
  }

  /**
   * Get hourly alert summary
   */
  async getHourlyAlerts(studyId: string, hours: number = 24): Promise<any[]> {
    const response = await apiClient.get(
      `/timescale/alerts/${studyId}/hourly`,
      { params: { hours } },
    );
    return response.data.data;
  }

  /**
   * Get alert counts by severity
   */
  async getAlertsBySeverity(
    studyId: string,
    hours: number = 24,
  ): Promise<AlertData[]> {
    const response = await apiClient.get(
      `/timescale/alerts/${studyId}/by-severity`,
      { params: { hours } },
    );
    return response.data.data;
  }

  /**
   * Get daily electrode quality summary
   */
  async getDailyElectrodeQuality(
    recordingId: string,
    days: number = 7,
  ): Promise<any[]> {
    const response = await apiClient.get(
      `/timescale/electrodes/${recordingId}/daily`,
      { params: { days } },
    );
    return response.data.data;
  }

  /**
   * Get current electrode status
   */
  async getElectrodeSummary(
    recordingId: string,
  ): Promise<ElectrodeQualityData[]> {
    const response = await apiClient.get(
      `/timescale/electrodes/${recordingId}/summary`,
    );
    return response.data.data;
  }

  /**
   * Get compression statistics (admin)
   */
  async getCompressionStats(): Promise<CompressionStats[]> {
    const response = await apiClient.get("/timescale/compression");
    return response.data.data;
  }

  /**
   * Manually trigger compression (admin)
   */
  async triggerCompression(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post("/timescale/admin/compress");
    return response.data;
  }

  /**
   * Manually refresh continuous aggregates (admin)
   */
  async refreshAggregates(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post("/timescale/admin/refresh");
    return response.data;
  }
}

export const timescaleAPI = new TimescaleAPIClient();
export default timescaleAPI;
